import { FormControl, FormErrorMessage, FormHelperText, FormLabel, Input } from '@chakra-ui/react';
import { Controller, useFormContext } from 'react-hook-form';

import { DropdownInput, IntegrationAuthType } from '@aslanpay/portal-common-lib';

import { AUTH_FIELDS, AUTH_TYPE_OPTIONS } from './constants';
import { ConnectionFormValues } from './types';

interface AuthConfigFieldsProps {
  // When editing, secrets already stored for the unchanged auth type are optional (blank = keep).
  originalAuthType?: IntegrationAuthType;
  configuredSecretFields?: string[];
}

/** Auth type dropdown + the dynamic credential fields for the selected type. */
function AuthConfigFields({
  originalAuthType = undefined,
  configuredSecretFields = [],
}: AuthConfigFieldsProps) {
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext<ConnectionFormValues>();

  const authType = watch('authType');

  const isSecretStored = (fieldName: string) =>
    authType === originalAuthType && configuredSecretFields.includes(fieldName);

  return (
    <>
      <FormControl isInvalid={!!errors.authType} isRequired>
        <FormLabel>Authentication</FormLabel>
        <Controller
          control={control}
          name="authType"
          render={({ field: { onChange, onBlur, value } }) => (
            <DropdownInput
              key={value ?? 'NONE'}
              placeholder="Select an authentication type"
              options={AUTH_TYPE_OPTIONS}
              defaultValue={AUTH_TYPE_OPTIONS.find((o) => o.value === value)}
              onChange={onChange}
              onBlur={onBlur}
            />
          )}
        />
        <FormErrorMessage>{errors.authType?.message}</FormErrorMessage>
      </FormControl>

      {AUTH_FIELDS[authType]?.map((authField) => {
        const secretStored = authField.secret && isSecretStored(authField.name);
        return (
          <FormControl
            key={authField.name}
            isInvalid={!!errors[authField.name]}
            isRequired={authField.required && !secretStored}
          >
            <FormLabel>{authField.label}</FormLabel>
            <Input
              type={authField.secret ? 'password' : 'text'}
              autoComplete={authField.secret ? 'new-password' : 'off'}
              placeholder={secretStored ? '••••••••' : undefined}
              {...register(authField.name)}
            />
            {secretStored && (
              <FormHelperText>Currently set — leave blank to keep the stored value.</FormHelperText>
            )}
            <FormErrorMessage>{errors[authField.name]?.message as string}</FormErrorMessage>
          </FormControl>
        );
      })}
    </>
  );
}

export default AuthConfigFields;
